import {
  Typography as DSTypography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Close } from "@mui/icons-material"
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import React, { useMemo, useState } from "react"
import { useStripeProducts } from "../../hooks"
import { useAppDispatch, useAppSelector } from "../../redux"
import { AppActions } from "../../redux/appSlice"
import { BillingInterval } from "../../types"
import {
  getFilteredAndSortedProducts,
  hasMultipleBillingIntervals,
} from "../../utils/pricing"
import { PricingIntervalSwitch } from "../PricingIntervalSwitch"
import { ProductBox, SkeletonLoader } from "../productBox"

interface StripePaymentModalProps {}

export function StripePaymentModal({}: StripePaymentModalProps) {
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(
    BillingInterval.MONTH,
  )
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isModalShown = useAppSelector((state) => state.app.showStripeModal)
  const user = useAppSelector((state) => state.app.user)
  const dispatch = useAppDispatch()

  const { stripeProducts, loading, error, refetch } = useStripeProducts()

  const hasMultipleIntervals = useMemo(
    () => hasMultipleBillingIntervals(stripeProducts),
    [stripeProducts],
  )

  const products = useMemo(
    () =>
      getFilteredAndSortedProducts(
        stripeProducts,
        selectedInterval,
        user?.subscriptionPlan,
      ),
    [stripeProducts, selectedInterval, user?.subscriptionPlan],
  )

  const handleCloseModal = () => {
    dispatch(AppActions.updateShowStripeModal(false))
  }

  if (!isModalShown) return null

  return (
    <Box
      data-testid="stripe-modal"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(78, 64, 90, 0.40)",
      }}
    >
      <Box
        sx={{
          width: isMobile ? "95%" : "90%",
          height: isMobile ? "80%" : "100%",
          maxHeight: "80vh",
          backgroundColor: "white",
          borderRadius: "0.25rem",
          position: "relative",
          overflow: "auto",
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          padding={"var(--spacing-tokens-size-in-px-spacing-spacing-xl)"}
          marginTop={"var(--spacing-tokens-size-in-px-spacing-spacing-5xl)"}
        >
          <Box display={"flex"} flexDirection={"column"}>
            <DSTypography
              text="Subscription Plans"
              size={TypographySize.HeadingL}
              weight={TypographyWeight.Bold}
              color={"var(--colors-light-text-text-title)"}
            />
            <DSTypography
              text="Choose a plan that's right for you"
              size={TypographySize.TextS}
              weight={TypographyWeight.Regular}
              color={"var(--colors-light-text-text-paragraph)"}
            />
            {error && (
              <Box
                onClick={refetch}
                sx={{
                  cursor: "pointer",
                  marginTop:
                    "var(--spacing-tokens-size-in-px-spacing-spacing-s)",
                }}
              >
                <DSTypography
                  text={`${error} Click to retry.`}
                  size={TypographySize.TextS}
                  weight={TypographyWeight.Regular}
                  color={"var(--colors-light-text-text-error)"}
                />
              </Box>
            )}
          </Box>
          {!loading && hasMultipleIntervals && (
            <PricingIntervalSwitch
              selectedInterval={selectedInterval}
              onIntervalChange={setSelectedInterval}
            />
          )}
        </Box>
        <Box
          data-testid="stripe-products-container"
          display={"grid"}
          gridTemplateColumns={
            isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 0.33fr))"
          }
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={"1rem"}
          height={isMobile ? "auto" : "90%"}
          padding={"1rem"}
        >
          {loading ? (
            <SkeletonLoader />
          ) : products.length === 0 && !error ? (
            <Typography variant="body1" align="center" sx={{ width: "100%" }}>
              No products available.
            </Typography>
          ) : (
            !error &&
            products.map((product, index) => (
              <ProductBox
                key={index}
                product={product}
                selectedInterval={selectedInterval}
                sortedProducts={products}
                sx={{
                  width: "100%",
                  marginBottom: "1rem",
                  marginRight: isMobile ? 0 : "1rem",
                }}
              />
            ))
          )}
        </Box>
        <IconButton
          onClick={handleCloseModal}
          sx={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
        >
          <Close />
        </IconButton>
      </Box>
    </Box>
  )
}
